import classnames from 'classnames';
import { ID_BOOKMARK_TAB } from '@/shared/config';
import EventSelectedInfo from '@/features/edit-event-strip';
import ButtonTypes from './buttonTypes';
import EcgBookmark from './ecgBookmark';
import EcgViewerWrapper from './ecgViewerWrapper';
import HeartRateEventBox from './heartRateEventBox';
import HrChart from './hrChart';
import HrChartOptions from './hrChartOptions';
import StripEvents from './stripEvents';
import useWrapReport from './useWrapReport';

/**
 * Composes the ECG events tab, switching between the bookmark view, the heart-rate event box and the rhythm strip list. Reads study identifiers and processing flags from useWrapReport, returns the bookmark panel early when the active button is the bookmark tab, and otherwise renders the strips or HR event box beside the selected-event info, ECG viewer, HR chart and chart options, or an empty-data message when nothing was recorded.
 *
 * @returns The ECG events tab layout for the currently active sub-tab.
 */
const WrapReport = () => {
  const {
    keyRecoil,
    isEmptyData,
    activeButton,
    studyId,
    profileId,
    startReport,
    timezoneOffset,
    stopReport,
    ecgDataMap,
    hrChartRef,
    handleClickArrowButton,
    isProcessBeatEvent,
    isProcessMinMaxHr,
  } = useWrapReport();

  // activeTabState also allows TYPE_RHYTHM_EVENT_ENUM/TYPE_BEAT_EVENT_ENUM, none of which include ID_BOOKMARK_TAB's literal - atom-type gap.
  if ((activeButton as any) === ID_BOOKMARK_TAB) {
    return (
      <div className="rhythm-event-content --ecg-bookmark">
        <EcgBookmark activeButton={activeButton} />
      </div>
    );
  }

  return (
    <div className={classnames('rhythm-event-content', isProcessMinMaxHr ? '--hr' : '')}>
      {isProcessMinMaxHr ? (
        <HeartRateEventBox studyId={studyId} profileId={profileId} activeButton={activeButton} />
      ) : (
        <>
          <ButtonTypes />
          <StripEvents
            activeButton={activeButton}
            ecgDataMap={ecgDataMap}
            studyId={studyId}
            profileId={profileId}
            timezoneOffset={timezoneOffset}
            startReport={startReport}
          />
        </>
      )}
      <div className="rhythm-events-content-container --min-width-strip-event">
        {isEmptyData ? (
          <div className="empty-data-container">
            <div className="empty-data-text">There is no data recorded</div>
          </div>
        ) : (
          <>
            <div className="event-select-ecg-viewer-wrapper">
              <EventSelectedInfo
                keyRecoil={keyRecoil}
                componentId="rhythm-event-selected-info"
                timezoneOffset={timezoneOffset}
                ecgDataMap={ecgDataMap}
              />
              <EcgViewerWrapper
                activeButton={activeButton}
                isProcessBeatEvent={isProcessBeatEvent}
                ecgDataMap={ecgDataMap}
                timezoneOffset={timezoneOffset}
                profileId={profileId}
                studyId={studyId}
                handleClickArrowButton={handleClickArrowButton}
              />
            </div>
            <HrChart
              ref={hrChartRef}
              ecgDataMap={ecgDataMap}
              timezoneOffset={timezoneOffset}
              studyId={studyId}
              profileId={profileId}
            />
          </>
        )}
        <HrChartOptions
          profileId={profileId}
          timezoneOffset={timezoneOffset}
          studyId={studyId}
          startReport={startReport}
          stopReport={stopReport}
        />
      </div>
    </div>
  );
};

export default WrapReport;
